import { Formik, FormikConfig } from 'formik';
import { makeValidator } from 'mandle';
import * as React from 'react';

import { Redirect } from 'react-router-dom';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import * as R from 'fp-ts/Record';
import { constNull, pipe } from 'fp-ts/lib/function';
import { capitalize } from 'lodash-es';
import { ListSelector } from '../../components/ListSelector';
import { TextArea } from '../../components/TextArea';
import {
  MatchPlayerFragment,
  useCreateVoteMutation,
  Validation,
  VoteTypeFragmentFragment,
} from '../../generated/graphql';
import { makeInputPropsMaker } from '../../utils/inputPropsMaker';
import { required2 } from '../../validators';
import { Heading } from '../../components/Heading';
import { formatErrors } from '../../utils/formatErrors';
import { useAlert } from '../../contexts/AlertContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { SubmitButton } from '../../components/SubmitButton';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { paths } from '../../paths';
import { getVoteEmoji, getVoteLabel } from './voteLabelLookup';
import { useAlreadyCastVotes } from './useAlreadyCastVotes';

interface Props {
  matchId: string;
  players: MatchPlayerFragment[];
  voteType: VoteTypeFragmentFragment;
}

interface Fields {
  comment: string;
  matchId: string;
  playerId: string;
  voteTypeId: string;
}

const validate = makeValidator<Fields>({
  comment: [required2('Enter a comment')],
  playerId: [required2('Nominate a player')],
});

const voteHeading = {
  motm: `Your Man of the Match vote ${getVoteEmoji('motm')}`,
  dotd: `Your Dick of the Day vote ${getVoteEmoji('dotd')}`,
};

/**
 * Builds an error message from a validation response. Generally
 * only used for edge cases (e.g. when captain closes voting before
 * the form is submitted.)
 *
 * @param validationError
 */
const getValidationAlert = ({
  key,
  reason,
}: Pick<Validation, 'key' | 'reason'>): string => {
  if (key === 'match' && reason === 'is not open for voting') {
    return 'Voting has now closed for this match. Refresh the page to review the votes';
  }
  if (key === 'match_type_user') {
    return "You've already cast this vote for this match";
  }

  return `${capitalize(key)} ${reason}`;
};

export const VoteForm = ({ matchId, players, voteType }: Props) => {
  const [createVoteResponse, createVote] = useCreateVoteMutation();
  const alert = useAlert();
  const { setVoteCast } = useAlreadyCastVotes(matchId);

  const analytics = useAnalytics();

  if (createVoteResponse.data?.createVote?.entity) {
    return <Redirect to={paths.match.votes.vote(matchId)} />;
  }

  const initialValues: Fields = {
    comment: '',
    matchId,
    playerId: '',
    voteTypeId: voteType.id,
  };

  const submitVoteForm: FormikConfig<Fields>['onSubmit'] = (
    input,
    { setErrors },
  ) => {
    createVote({ input }).then((r) => {
      pipe(
        r.data?.createVote,
        O.fromNullable,
        O.map(async (voteData) => {
          if (voteData.validation) {
            analytics.track('Data Submit Failed', {
              form: 'cast_vote',
              errors: voteData.validation,
            });
            setErrors(formatErrors(voteData.validation));
            // If any errors are present that are not keyed to a
            // field in the form, present the first in an alert
            pipe(
              voteData.validation,
              A.findFirst((v) => !Object.keys(initialValues).includes(v.key)),
              O.map(getValidationAlert),
              O.fold(constNull, (error) => alert(error, 'danger')),
            );
            return;
          }

          alert('Vote cast 🗳');
          analytics.track('Form Submitted', {
            form: 'cast_vote',
            type: voteType,
          });
          setVoteCast(voteType.id);
          return;
        }),
      );
    });
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validate={validate}
      onSubmit={submitVoteForm}
    >
      {(formProps) => {
        const getInputProps = makeInputPropsMaker(formProps);
        return (
          <form
            onSubmit={formProps.handleSubmit}
            noValidate={true}
            className="space-y-6"
          >
            <Heading className="text-2xl">
              {pipe(
                voteHeading,
                R.lookup(voteType.name),
                O.getOrElse(() => voteType.name),
              )}
            </Heading>
            <TextArea
              label="Comment"
              id="comment"
              {...getInputProps('comment')}
              placeholder="Tell us what you really think about your (team)mates&hellip;"
              autoFocus
            />

            <Heading level={3}>
              Who's your {getVoteLabel(voteType.name)}?
            </Heading>

            <ListSelector
              label="Choose player"
              onChange={formProps.handleChange('playerId')}
              value={formProps.values.playerId}
              error={
                formProps.touched.playerId
                  ? formProps.errors.playerId
                  : undefined
              }
              options={players.map((player) => ({
                name: (
                  <div className="flex items-center">
                    <PlayerAvatar player={player} className="mr-2" />
                    {player.name}
                  </div>
                ),
                value: player.id,
                searchValue: player.name,
              }))}
              showFilter={players.length > 15}
            />

            <SubmitButton
              type="submit"
              loading={createVoteResponse.fetching}
              icon={'check'}
            >
              Cast vote
            </SubmitButton>
          </form>
        );
      }}
    </Formik>
  );
};
